import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import AIDisclaimerText from '../sideBarComponent/AIDisclaimerText';
import { deepResearchBrainIcon, deepResearchSearchIcon } from '../../commonImage';
import LoadingIndicator from '../openAIChat/LoadingIndicator';
import { useDispatch, useSelector } from 'react-redux';

import {
  setActivityPanelActiveTab,
  setActivityPanelTitle,
  setShowActivityPanel
} from '../../store/actions/deepResearchActions';
import CloseIcon from '../../assets/images/deep-research/close-icon.svg';
import { getFaviconUrl, handleFaviconError, removeQuotes } from './Utils';
import CitationList from './CitationList';

const ActivityPanel: React.FC = () => {
  const dispatch = useDispatch();
  const activityPanelTitle = useSelector((state: any) => state.deepResearch.activityPanelTitle);
  const currentTabValue = useSelector((state: any) => state.deepResearch.activityPanelActiveTab);
  const checkIframe = window.location !== window.parent.location;
  const [activeTab, setActiveTab] = useState<'activity' | 'sources'>('activity');
  const activityPanelRef = useRef<HTMLDivElement>(null);
  const sourcesPanelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setActiveTab(currentTabValue);
  }, [currentTabValue]);

  useEffect(() => {
    if (activityPanelRef.current) {
      activityPanelRef.current.scrollTop = activityPanelRef.current.scrollHeight;
    }
  }, [activityPanelTitle]);

  const focusedMessage = useSelector((state: any) => state.deepResearch.focusedMessage);
  const [focusedMessageResponse, setFocusedMessageResponse] = useState<any>(null);
  const isAPILoading = useSelector((state: any) => state.deepResearch.isLoaderEnabled);
  const isProgressCardVisible = useSelector(
    (state: any) => state.deepResearch.isProgressCardVisible
  );
  const [isResponseInProgress, setIsResponseInProgress] = useState(false);

  useEffect(() => {
    setIsResponseInProgress(isAPILoading || isProgressCardVisible);
  }, [isAPILoading, isProgressCardVisible]);

  useEffect(() => {
    let formattedResponse: any;
    try {
      formattedResponse = JSON.parse(focusedMessage?.response);
    } catch (e) {}
    if (formattedResponse?.activityPanelTitle?.length > 0)
      dispatch(setActivityPanelTitle(removeQuotes(formattedResponse?.activityPanelTitle)));
    setFocusedMessageResponse(formattedResponse);
  }, [focusedMessage]);

  const handleTabChange = (tab: 'activity' | 'sources') => {
    dispatch(setActivityPanelActiveTab(tab));
  };

  const handleOnClose = () => {
    dispatch(setShowActivityPanel(false));
  };

  const renderAllSourcesList = (sourcesList: any[]) => {
    if (sourcesList?.length > 0) {
      const domainMap = new Map<string, { count: number; favicon: string }>();
      sourcesList.forEach((c) => {
        if (c.url) {
          const domain = new URL(c.url).hostname.replace('www.', '');
          const favicon = getFaviconUrl({ url: c.url });
          if (domainMap.has(domain)) {
            domainMap.get(domain)!.count += 1;
          } else {
            domainMap.set(domain, { count: 1, favicon });
          }
        }
      });

      return Array.from(domainMap.entries()).map(([domain, { count, favicon }], idx) => (
        <div key={idx} className='all-source-item'>
          <img
            src={favicon}
            alt={domain}
            className='all-source-favicon'
            onError={handleFaviconError}
          />
          <span className='all-source-domain'>{domain}</span>
          <span className='all-source-count'>{count}</span>
        </div>
      ));
    }
  };

  const renderResearchCompletionInfo = (target: any, isFromThreadView?: boolean) => {
    let responseObj: any = target;
    if (responseObj && typeof responseObj.response === 'string') {
      try {
        responseObj = JSON.parse(responseObj.response);
      } catch {
        responseObj = {};
      }
    }

    return (
      <p
        className={`deep-research-status-text ${isFromThreadView ? 'thread-view-status-text' : ''}`}>
        Research completed in {responseObj?.researchTime} ·{' '}
        <span onClick={() => handleTabChange('sources')}>
          {responseObj?.sources?.length} sources
        </span>{' '}
        ·{' '}
        <span onClick={() => handleTabChange('activity')}>
          {responseObj?.activities?.length} searches
        </span>
      </p>
    );
  };

  return (
    <StyledActivityPanel>
      <aside className='deep-research-sidebar' aria-label='Research sidebar'>
        <div className='deep-research-sidebar-tabs-row'>
          <div className='deep-research-sidebar-tabs'>
            <button
              className={activeTab === 'activity' ? 'active' : ''}
              onClick={() => handleTabChange('activity')}
              aria-selected={activeTab === 'activity'}
              aria-controls='activity-panel'
              tabIndex={0}>
              Activity
            </button>
            <button
              className={activeTab === 'sources' ? 'active' : ''}
              onClick={() => handleTabChange('sources')}
              aria-selected={activeTab === 'sources'}
              aria-controls='sources-panel'
              tabIndex={0}>
              {focusedMessageResponse?.sources?.length > 0
                ? focusedMessageResponse?.sources.length
                : ''}{' '}
              Sources
            </button>
          </div>
          <button
            className='deep-research-sidebar-close'
            aria-label='Close sidebar'
            onClick={handleOnClose}
            tabIndex={0}>
            <img src={CloseIcon} alt='Close sidebar' />
          </button>
        </div>
        <hr></hr>
        <div className='deep-research-sidebar-content'>
          {activeTab === 'activity' ? (
            <div id='activity-panel' role='tabpanel' ref={activityPanelRef}>
              <ul className='deep-research-activity-list'>
                <h4 className='activity-title'>{activityPanelTitle}</h4>
                {focusedMessageResponse?.activities &&
                  focusedMessageResponse?.activities.map((item: any, idx: number) => {
                    // event:search
                    if (item.type === 'search') {
                      return (
                        <li key={idx} className='activity-search'>
                          <span className='activity-icon'>
                            <img src={deepResearchSearchIcon} alt='search-icon' />
                          </span>
                          <span className='activity-text'>{item.text}</span>
                        </li>
                      );
                    }
                    // event:search_web with URL
                    if (item.type === 'search_web' && item.url) {
                      const favicon = getFaviconUrl(item);
                      return (
                        <li key={idx} className='activity-search search_web'>
                          <span className='activity-icon'>
                            <img
                              className='website-fav-img'
                              src={favicon}
                              alt={item.domain}
                              onError={handleFaviconError}
                            />
                          </span>
                          <span className='activity-text'>
                            Read{' '}
                            <a
                              href={item.url}
                              target='_blank'
                              rel='noopener noreferrer'
                              className='activity-link'>
                              {item.domain}
                            </a>
                          </span>
                        </li>
                      );
                    }
                    // event:reasoning
                    if (item.type === 'reasoning') {
                      return (
                        <li key={idx} className='activity-reasoning'>
                          <span className='activity-icon'>
                            <img src={deepResearchBrainIcon} alt='openai-icon' />
                          </span>
                          <span className='activity-text'>{item.text}</span>
                        </li>
                      );
                    }
                  })}
                {isResponseInProgress && (
                  <>
                    <LoadingIndicator />
                    <br></br>
                    <br></br>
                  </>
                )}
                {!isResponseInProgress &&
                  renderResearchCompletionInfo(focusedMessageResponse, false)}
              </ul>
            </div>
          ) : (
            <div id='sources-panel' role='tabpanel' ref={sourcesPanelRef}>
              <div className='deep-research-citations-list'>
                {focusedMessageResponse?.citations?.length > 0 && (
                  <CitationList citations={focusedMessageResponse?.citations} />
                )}
                <div className='deep-research-all-sources-section'>
                  <h4 className='all-sources-title'>
                    {isResponseInProgress ? 'Compiling Sources ...' : 'All Sources'}
                  </h4>
                  <div className='all-sources-list'>
                    {renderAllSourcesList(focusedMessageResponse?.sources)}
                  </div>
                </div>
              </div>
            </div>
          )}
          <div className='borderTop'>
            <div className={checkIframe ? 'AITaxonomyTeams' : 'AITaxonomy'}>
              {' '}
              <AIDisclaimerText />
            </div>
          </div>
        </div>
      </aside>
    </StyledActivityPanel>
  );
};

const StyledActivityPanel = styled.div`
  .deep-research-sidebar {
    display: flex;
    flex-direction: column;
    width: 340px;
    min-width: 280px;
    max-width: 400px;
    background: #fff;
    border-left: 1px solid #eee;
    height: auto;
    box-shadow: -2px 0 8px rgba(0, 0, 0, 0.03);
    z-index: 1000;
    position: relative;
  }
  .deep-research-sidebar hr {
    border: none;
    border-top: 1.5px solid #e0e0e0;
    margin: 10px 0 0 0;
    width: 100%;
  }
  .deep-research-sidebar-tabs-row {
    display: flex;
    align-items: center;
    padding: 12px 16px 0 16px;
  }
  .deep-research-sidebar-tabs {
    display: flex;
    flex: 1;
    background: #f3f3f3;
    border-radius: 28px;
    overflow: hidden;
    height: auto;
    padding: 5px;
  }
  .deep-research-sidebar-tabs button {
    flex: 1;
    border: none;
    background: transparent;
    font-size: 1.1rem;
    padding: 8px 18px;
    cursor: pointer;
    color: #222;
    transition: background 0.2s;
  }
  .deep-research-sidebar-tabs button.active {
    background: #fff;
    font-weight: 600;
    color: #111;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04);
    border-radius: 24px;
  }
  .deep-research-sidebar-close {
    display: flex;
    align-items: center;
    justify-content: center;
    background: transparent;
    border: none;
    font-size: 2rem;
    color: #222;
    margin-left: 12px;
    cursor: pointer;
    line-height: 1;
    padding: 0 8px;
    transition: background 0.2s;
    height: 34px;
    width: 34px;
  }
  .deep-research-sidebar-close:hover {
    background: #f3f3f3;
    border-radius: 50%;
    height: 34px;
    width: 34px;
  }
  .deep-research-activity-list {
    list-style: none;
    padding: 0px 20px;
    margin: 0;
    color: black;
    height: 130vh;
    overflow-y: auto;
  }
  .deep-research-citations-list {
    list-style: none;
    margin: 0;
    color: black;
    height: 130vh;
    overflow-y: auto;
  }
  .deep-research-citations-list ul {
    padding: 5px 18px;
    margin-top: 0px;
  }

  .activity-text {
    vertical-align: middle;
    word-wrap: break-word;
    color: #2e2e38;
    font-family: 'ey_interstate';
    font-size: 12px;
    font-style: normal;
    font-weight: 300;
    line-height: 20px;
    max-width: 95%;
  }
  .activity-link {
    text-decoration: none;
    color: #0f69ae;
  }
  .icon-title-div {
    display: flex;
    align-items: flex-start;
    margin-bottom: 1rem;
  }

  .activity-reasoning,
  .activity-search {
    margin-bottom: 10px;
    display: flex;
  }
  .activity-icon {
    min-width: 16px;
    margin-right: 8px;
    font-size: 1.2em;
    vertical-align: middle;
  }
  .activity-title {
    color: #000;
    font-family: 'ey_interstate';
    font-size: 12px;
    font-style: normal;
    font-weight: 400;
    line-height: 20px;
  }
  .activity-summary {
    margin-bottom: 12px;
  }
  .activity-search {
    color: #222;
    margin-bottom: 16px;
  }
  .activity-website {
    display: flex;
    align-items: flex-start;
  }
  .activity-website a {
    color: #2a5db0;
    text-decoration: underline;
    font-weight: bold;
  }

  .deep-research-open-ai-icon {
    height: 1.5rem;
    width: 1.5rem;
  }
  .deep-research-search-icon {
    height: 1.5rem;
    width: 1.5rem;
  }
  .website-fav-img {
    min-width: 16px;
    max-height: 16px;
    vertical-align: middle;
  }
  .deep-research-all-sources-section {
    padding: 0px 1.5rem;
  }

  .all-sources-title {
    font-size: 1.2rem;
    font-weight: 400;
    margin-bottom: 1rem;
    color: #4e4e5d;
  }

  .all-sources-list {
    display: flex;
    flex-wrap: wrap;
  }

  .all-source-item {
    display: flex;
    align-items: center;
    margin-bottom: 1.2rem;
    min-width: 180px;
  }

  .all-source-favicon {
    width: 16px;
    height: 16px;
    margin-right: 0.7rem;
    border-radius: 4px;
    background: transparent;
  }

  .all-source-domain {
    font-size: 12px;
    font-weight: 400;
    color: #23272f;
    font-family: 'ey_interstate';
  }

  .all-source-count {
    display: inline-block;
    width: 20px;
    height: 20px;
    line-height: 20px;
    background: #f0f1f4;
    color: #4e4e5d;
    font-size: 12px;
    font-weight: 600;
    border-radius: 50px;
    text-align: center;
    margin-left: 0.2rem;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
  }
  .borderTop {
    border-top: 1px solid #e7e7ea;
  }

  .AITaxonomy {
    padding: 0 10px;
  }
  .deep-research-status-text {
    color: #7f7f91;
    font-family: 'EYInterstate';
    font-size: 14px;
    font-weight: 700;
    line-height: 20px;
    cursor: pointer;
  }
`;

export default ActivityPanel;
